import { zodResolver } from '@hookform/resolvers/zod';
import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { CrmTaskStatus } from '@plato/data/prisma';
import { DialogForm } from '@plato/ui-legacy/components/dialog/dialog-form';
import { ModalForm } from '@plato/ui-legacy/components/form-components/modal-form';
import { Label } from '@plato/ui-legacy/components/ui/label';
import { useDiscardChangesWarning } from '@plato/ui-legacy/lib/hooks/use-discard-changes-warning';
import { getTextContent } from '@plato/ui-legacy/lib/rich-text/get-text-content';
import { plainTextToLexicalSync } from '@plato/ui-legacy/lib/rich-text/plain-text-to-lexical';
import { PickerValueSuggestion } from '@plato/ui/components/picker-value';
import { Dialog } from '@plato/ui/containers/dialog';
import { ArrowUpRight, CalendarIcon, CircleUserRoundIcon, PlusIcon } from 'lucide-react';
import { useMemo, type ReactNode } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useVoiceRecording } from '../../../lib/contexts/voice-recording-context.tsx';
import { crmTaskInputDtoSchema, type CrmTaskIntent } from '../../../server/dto/crm-task-input-dto.ts';
import { FileUploader } from '../../crm-attachment/components/file-uploader.tsx';
import { RichTextInput } from '../../rich-text/components/rich-text-input.tsx';
import { UsersSelect } from '../../shared/components/form-items-with-fetch/users-select.tsx';
import { DueDatePicker, TaskLinksInput } from './task-form-fields.tsx';

function createTaskBulkFormValuesSchema(assigneeRequiredMessage: string) {
    return crmTaskInputDtoSchema.omit({ assignedToId: true }).extend({
        assignedToIds: z.array(z.string().uuid()).min(1, { message: assigneeRequiredMessage }),
    });
}

export type TaskBulkFormValues = z.infer<ReturnType<typeof createTaskBulkFormValuesSchema>>;

/** A ready-made task the form can be filled with in one tap. */
export type TaskFormSuggestion = {
    label: string;
    /** Plain text that becomes the description when the chip is picked. */
    description: string;
    taskIntent: CrmTaskIntent;
};

type TaskBulkFormDialogProps = {
    open: boolean;
    onClose: () => void;
    onSubmit: (values: TaskBulkFormValues) => void;
    isSubmitting: boolean;
    title: string;
    initialValues?: Partial<TaskBulkFormValues>;
    /** Offered under the description while it is empty; filling it in puts them away. */
    suggestions?: TaskFormSuggestion[];
};

export function TaskBulkFormDialog({
    open,
    onClose,
    onSubmit,
    isSubmitting,
    title,
    initialValues,
    suggestions,
}: TaskBulkFormDialogProps): ReactNode {
    const { t } = useTranslation('tasks');
    const { enableFileUpload } = useAppConfig();
    const { currentSessionId } = useVoiceRecording();

    const defaultValues = useMemo(() => {
        return {
            dueDate: new Date(),
            links: [],
            assignedToIds: [],
            status: CrmTaskStatus.OPEN,
            description: undefined,
            attachments: [],
            ...initialValues,
        };
    }, [initialValues]);

    const schema = useMemo(
        () => createTaskBulkFormValuesSchema(t('form__assignee_required', { ns: 'common' })),
        [t],
    );

    const form = useForm<TaskBulkFormValues>({
        defaultValues,
        resolver: zodResolver(schema),
    });

    const {
        control,
        handleSubmit,
        setValue,
        formState: { isDirty, errors: formErrors },
    } = form;

    const assigneeCount = useWatch({ control, name: 'assignedToIds' }).length;

    const submitText = t('tasks__form_create_submit', { count: assigneeCount });

    const description = useWatch({ control, name: 'description' });
    const showSuggestions = useMemo(() => !getTextContent(description), [description]);

    const applySuggestion = (suggestion: TaskFormSuggestion): void => {
        // Only blank text converts to nothing, and a suggestion always carries some.
        const richText = plainTextToLexicalSync(suggestion.description);
        if (!richText) {
            return;
        }

        setValue('description', richText, { shouldDirty: true, shouldValidate: true });
        setValue('taskIntent', suggestion.taskIntent, { shouldDirty: true });
    };

    const { confirmDiscard } = useDiscardChangesWarning(isDirty);

    const handleClose = (): void => {
        confirmDiscard(() => {
            onClose();
        });
    };

    const submit = handleSubmit((values) => {
        onSubmit(values);
    });

    return (
        <Dialog open={open} onClose={handleClose} disablePointerDismissal>
            <DialogForm>
                <DialogForm.Header title={title} />
                <DialogForm.Content>
                    <DialogForm.ItemList>
                        <DialogForm.Item>
                            <Label htmlFor="description" className="sr-only">
                                {t('description')}
                            </Label>
                            <DialogForm.Body>
                                <Controller
                                    control={control}
                                    name="description"
                                    render={({ field }) => (
                                        <RichTextInput
                                            trackingContext={{ source: 'task' }}
                                            placeholder={t('tasks__description_placeholder')}
                                            {...field}
                                        />
                                    )}
                                />
                            </DialogForm.Body>
                            <DialogForm.Error error={formErrors.description} />
                        </DialogForm.Item>
                        {suggestions && showSuggestions && (
                            <div className="flex flex-wrap gap-tight">
                                {suggestions.map((suggestion) => (
                                    <PickerValueSuggestion
                                        key={suggestion.label}
                                        label={suggestion.label}
                                        onClick={() => {
                                            applySuggestion(suggestion);
                                        }}
                                    >
                                        <PlusIcon />
                                    </PickerValueSuggestion>
                                ))}
                            </div>
                        )}
                    </DialogForm.ItemList>
                    <DialogForm.Group>
                        <DialogForm.ItemList subgrid>
                            <DialogForm.Item>
                                <DialogForm.Label icon={ArrowUpRight}>
                                    {t('related_to', {
                                        ns: 'common',
                                    })}
                                </DialogForm.Label>
                                <DialogForm.Body className="flex-col">
                                    <Controller
                                        control={control}
                                        name="links"
                                        render={({ field }) => <TaskLinksInput {...field} />}
                                    />
                                </DialogForm.Body>
                                <ModalForm.Error errorMessage={formErrors.links} />
                            </DialogForm.Item>

                            <DialogForm.Item>
                                <DialogForm.Label htmlFor="dueDate" icon={CalendarIcon}>
                                    {t('due_date', {
                                        ns: 'common',
                                    })}
                                </DialogForm.Label>
                                <DialogForm.Body>
                                    <Controller
                                        control={control}
                                        name="dueDate"
                                        render={({ field }) => <DueDatePicker {...field} />}
                                    />
                                </DialogForm.Body>
                                <DialogForm.Error error={formErrors.dueDate} />
                            </DialogForm.Item>
                            <DialogForm.Item className="items-start">
                                <DialogForm.Label htmlFor="assignedToIds" icon={CircleUserRoundIcon} required>
                                    {t('assigned_to', {
                                        ns: 'common',
                                    })}
                                </DialogForm.Label>
                                <DialogForm.Body className="min-w-0 flex-col gap-1">
                                    <Controller
                                        control={control}
                                        name="assignedToIds"
                                        render={({ field }) => (
                                            <UsersSelect
                                                multiple
                                                {...field}
                                                error={!!formErrors.assignedToIds}
                                                className="w-full justify-start"
                                            />
                                        )}
                                    />
                                    <DialogForm.Error error={formErrors.assignedToIds} />
                                </DialogForm.Body>
                            </DialogForm.Item>
                        </DialogForm.ItemList>
                        {enableFileUpload && (
                            <DialogForm.ItemList subgrid>
                                <Controller
                                    control={control}
                                    name="attachments"
                                    render={({ field }) => (
                                        <FileUploader
                                            {...field}
                                            value={field.value ?? []}
                                            analyticsContext="task"
                                        />
                                    )}
                                />
                            </DialogForm.ItemList>
                        )}
                    </DialogForm.Group>
                </DialogForm.Content>
                <DialogForm.SubmitBlock
                    isLoading={isSubmitting}
                    onCancel={handleClose}
                    onSubmit={submit}
                    submitText={submitText}
                    disabled={!!currentSessionId}
                />
            </DialogForm>
        </Dialog>
    );
}
